import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Space, Tag, Descriptions, Avatar, List, message, Empty } from 'antd';
import { ArrowRightOutlined, EditOutlined, NotificationOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { noticesApi } from '../../utils/api';
import { DetailPageSkeleton } from '../../components/SkeletonLoaders';
import { getImagePath } from '../../utils/imageHelper';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';

const ViewNotice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await noticesApi.getById(id);
        setData(res?.data ?? res);
      } catch (e) {
        message.error(e?.message || 'خطا در دریافت اعلان');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <MainLayout>
        <DetailPageSkeleton />
      </MainLayout>
    );
  }

  const notice = data?.notice;
  if (!notice) {
    return (
      <MainLayout>
        <Empty description="اعلان یافت نشد" />
      </MainLayout>
    );
  }

  const readMembers = data?.readMembers || [];
  const unreadObj = data?.unReadMembers || {};
  const unreadMembers = Array.isArray(unreadObj) ? unreadObj : Object.values(unreadObj);

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                <NotificationOutlined className="text-indigo-600 dark:text-indigo-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">{notice.heading}</h1>
              </div>
            </div>
            <Space>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(returnToRef.current || '/notices', { state: buildGoBackState() })}
              >
                بازگشت
              </Button>
              <PermissionGate permission="edit_notice">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  className={PANEL_PRIMARY_BTN_CLASS}
                  onClick={() =>
                    navigate(`/notices/${id}/edit`, {
                      state: { returnTo: returnToRef.current, ...buildGoBackState() },
                    })
                  }
                >
                  ویرایش
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 space-y-4">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
                <Descriptions.Item label="عنوان" span={2}>
                  {notice.heading}
                </Descriptions.Item>
                <Descriptions.Item label="ارسال برای">
                  <Tag color={notice.to === 'client' ? 'purple' : 'blue'}>
                    {notice.to === 'client' ? 'مشتری' : 'کارمند'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="تاریخ">{notice.notice_date || '—'}</Descriptions.Item>
                <Descriptions.Item label="توضیحات" span={2}>
                  <div className="whitespace-pre-wrap">{notice.description || '—'}</div>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="اعضای خوانده‌شده" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <List
                dataSource={readMembers}
                locale={{ emptyText: 'موردی ندارد' }}
                renderItem={(m) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar src={getImagePath(m.user?.image_url)}>{(m.user?.name || '?').charAt(0)}</Avatar>}
                      title={m.user?.name || '—'}
                    />
                  </List.Item>
                )}
              />
            </Card>

            <Card title="اعضای خوانده‌نشده" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <List
                dataSource={unreadMembers}
                locale={{ emptyText: 'موردی ندارد' }}
                renderItem={(m) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar src={getImagePath(m.user?.image_url)}>{(m.user?.name || '?').charAt(0)}</Avatar>}
                      title={m.user?.name || '—'}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewNotice;
